import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { useI18n } from "@/context/I18nContext";

/**
 * Pre-start / running snapshot of the effective RiskSentinel + adaptive
 * sizing recommendations per active exchange (iter36 — Feb 2026, msg 505+).
 *
 * Shows EXACTLY what the engine will use this cycle:
 *  • Configured USD cap vs configured % of equity → effective daily loss
 *  • Baseline (starting_equity + timestamp)
 *  • Live equity aggregation, per-exchange price source
 *  • Recommended layers × $/layer per exchange, capital utilization
 *  • Liquidity regime + hard_fail reason when applicable
 */
export default function AdaptiveSizingPanel() {
  const { t } = useI18n();
  const [risk, setRisk] = useState(null);
  const [sizing, setSizing] = useState(null);
  const [err, setErr] = useState("");

  const load = async () => {
    try {
      const [r, s] = await Promise.all([
        api.get("/diagnostics/effective-risk"),
        api.get("/diagnostics/adaptive-sizing"),
      ]);
      setRisk(r.data);
      setSizing(s.data);
      setErr("");
    } catch (e) {
      setErr(e.response?.data?.detail || e.message);
    }
  };
  useEffect(() => {
    load();
    const id = setInterval(load, 5000);
    return () => clearInterval(id);
  }, []);

  if (err) {
    return (
      <div className="mq-panel p-3" data-testid="adaptive-panel-error">
        <div className="mq-label">{t("adaptivePanelTitle")}</div>
        <div className="text-[11px] mt-2" style={{ color: "var(--mq-red)" }}>{err}</div>
      </div>
    );
  }
  if (!risk || !sizing) {
    return (
      <div className="mq-panel p-3" data-testid="adaptive-panel-loading">
        <div className="mq-label">{t("adaptivePanelTitle")}</div>
        <div className="text-[11px] mt-2 text-[var(--mq-text-3)]">{t("noData")}</div>
      </div>
    );
  }
  const bySource = Object.entries(risk.price_source_by_ex || {});
  const byEx = Object.entries(sizing.by_exchange || {});
  return (
    <div className="mq-panel" data-testid="adaptive-panel">
      <div className="mq-panel-header">
        <span className="mq-heading text-sm font-semibold tracking-wider">{t("adaptivePanelTitle")}</span>
        <span className="mq-label">{byEx.length} EX</span>
      </div>
      <div className="p-3 grid grid-cols-2 sm:grid-cols-4 gap-3 text-xs font-mono">
        <div>
          <div className="mq-label">{t("apRiskCap")}</div>
          <div data-testid="ap-risk-cap">${risk.configured_usd_cap?.toFixed(2)}</div>
        </div>
        <div>
          <div className="mq-label">{t("apRiskPct")}</div>
          <div data-testid="ap-risk-pct">{risk.configured_pct_of_equity?.toFixed(2)} %</div>
        </div>
        <div>
          <div className="mq-label">{t("apEffectiveLoss")}</div>
          <div
            data-testid="ap-effective-loss"
            style={{ color: "var(--mq-yellow)", fontWeight: 600 }}
          >
            ${risk.effective_daily_loss?.toFixed(2)}
          </div>
        </div>
        <div>
          <div className="mq-label">{t("apStartingEquity")}</div>
          <div data-testid="ap-starting-equity">${risk.starting_equity?.toFixed(2)}</div>
        </div>
        <div>
          <div className="mq-label">{t("apCurrentEquity")}</div>
          <div data-testid="ap-current-equity">
            {risk.current_equity_usd == null ? "—" : `$${Number(risk.current_equity_usd).toFixed(2)}`}
          </div>
        </div>
        <div>
          <div className="mq-label">{t("apDailyPnl")}</div>
          <div
            data-testid="ap-daily-pnl"
            style={{ color: (risk.daily_pnl_usd || 0) < 0 ? "var(--mq-red)" : "var(--mq-green)" }}
          >
            {(risk.daily_pnl_usd || 0) >= 0 ? "+" : ""}${(risk.daily_pnl_usd || 0).toFixed(2)}
          </div>
        </div>
        <div>
          <div className="mq-label">{t("apBaselineTs")}</div>
          <div data-testid="ap-baseline-ts" className="text-[10px]">
            {risk.baseline_ts_iso ? new Date(risk.baseline_ts_iso).toLocaleTimeString() : "—"}
          </div>
        </div>
        <div>
          <div className="mq-label">{t("apEquityUnavail")}</div>
          <div
            data-testid="ap-equity-unavailable"
            style={{ color: risk.equity_unavailable ? "var(--mq-red)" : "var(--mq-green)" }}
          >
            {risk.equity_unavailable ? "EQUITY_PRICE_UNAVAILABLE" : "OK"}
          </div>
        </div>
      </div>

      {bySource.length > 0 && (
        <div className="px-3 pb-3 text-[11px] font-mono">
          <span className="mq-label">{t("apPriceSources")}: </span>
          {bySource.map(([n, s]) => (
            <span key={n} className="ml-2">
              {n}=<span style={{ color: "var(--mq-cyan)" }}>{s}</span>
            </span>
          ))}
        </div>
      )}

      <div className="border-t" style={{ borderColor: "var(--mq-border)" }}>
        <div className="px-3 py-2 mq-label" style={{ color: "var(--mq-cyan)" }}>
          {t("apPerExchange")}
        </div>
        {byEx.length === 0 ? (
          <div className="px-3 pb-3 text-[11px] text-[var(--mq-text-3)] italic">
            {t("noActiveExchange")}
          </div>
        ) : (
          <table className="w-full text-xs font-mono">
            <thead>
              <tr className="text-left mq-label">
                <th className="px-3 py-1">{t("exchange")}</th>
                <th className="px-2 py-1">{t("apRegime")}</th>
                <th className="px-2 py-1 text-right">{t("apLayers")}</th>
                <th className="px-2 py-1 text-right">{t("apPerLayer")}</th>
                <th className="px-2 py-1 text-right">{t("apDeploy")}</th>
                <th className="px-2 py-1 text-right">{t("apUtil")}</th>
                <th className="px-2 py-1">{t("apStatus")}</th>
              </tr>
            </thead>
            <tbody>
              {byEx.map(([n, s]) => (
                <tr key={n} data-testid={`ap-row-${n}`} className="border-t" style={{ borderColor: "var(--mq-border)" }}>
                  <td className="px-3 py-1 uppercase">{n}</td>
                  <td className="px-2 py-1">{s.liquidity_regime}</td>
                  <td className="px-2 py-1 text-right">{s.layers}</td>
                  <td className="px-2 py-1 text-right">${Number(s.usd_per_layer).toFixed(4)}</td>
                  <td className="px-2 py-1 text-right">${Number(s.total_deploy_usd).toFixed(4)}</td>
                  <td className="px-2 py-1 text-right">{Number(s.capital_utilization_pct).toFixed(1)} %</td>
                  <td
                    className="px-2 py-1 uppercase"
                    style={{ color: s.hard_fail ? "var(--mq-red)" : "var(--mq-green)" }}
                  >
                    {s.hard_fail || "OK"}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>
    </div>
  );
}
